import { computed, reactive } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import ChatAPI from 'custom/api/chat';

export const CHAT_EVENTS = {
  MESSAGE_CREATED: 'loco.chat.message.created',
  MESSAGE_UPDATED: 'loco.chat.message.updated',
  CHANNEL_UPDATED: 'loco.chat.channel.updated',
  TYPING: 'loco.chat.typing',
};

const TYPING_TTL_MS = 6000;

// One store for the sidebar, the channel views and desktop notifications
const state = reactive({
  channels: [],
  loaded: false,
  activeChannelId: null,
  currentUserId: null,
  typing: {}, // channelId → { userId: { name, until } }
  listening: false,
});

let notify = null;

const load = async () => {
  const { data } = await ChatAPI.channels();
  state.channels = data.payload;
  state.loaded = true;
};

const findChannel = id => state.channels.find(channel => channel.id === id);

const upsertChannel = channel => {
  const index = state.channels.findIndex(item => item.id === channel.id);
  if (index === -1) state.channels.unshift(channel);
  else state.channels.splice(index, 1, channel);
};

const onMessageCreated = message => {
  const channel = findChannel(message.channel_id);
  if (!channel) {
    load();
    return;
  }
  channel.last_message_at = message.created_at;
  const mine = message.sender?.id === state.currentUserId;
  if (mine || message.parent_id) return;

  const viewing = state.activeChannelId === channel.id && !document.hidden;
  const mentioned = (message.content_attributes?.mentions || []).includes(
    state.currentUserId
  );
  if (!viewing) {
    channel.unread_count = (channel.unread_count || 0) + 1;
    if (mentioned) channel.mention_count = (channel.mention_count || 0) + 1;
  }
  const wants =
    channel.notifications === 'all' ||
    (channel.notifications === 'mentions' && mentioned);
  if (!viewing && wants && notify) notify(channel, message);
};

const onTyping = ({ channel_id: channelId, user }) => {
  state.typing[channelId] ||= {};
  state.typing[channelId][user.id] = {
    name: user.name,
    until: Date.now() + TYPING_TTL_MS,
  };
  setTimeout(() => {
    const entry = state.typing[channelId]?.[user.id];
    if (entry && entry.until <= Date.now()) {
      delete state.typing[channelId][user.id];
    }
  }, TYPING_TTL_MS + 100);
};

const listen = () => {
  if (state.listening) return;
  state.listening = true;
  emitter.on(CHAT_EVENTS.MESSAGE_CREATED, onMessageCreated);
  emitter.on(CHAT_EVENTS.CHANNEL_UPDATED, load);
  emitter.on(CHAT_EVENTS.TYPING, onTyping);
};

export function useChat({ currentUserId, onNotify } = {}) {
  if (currentUserId) state.currentUserId = currentUserId;
  if (onNotify) notify = onNotify;
  listen();
  if (!state.loaded) load().catch(() => {});

  const joined = computed(() =>
    state.channels.filter(channel => channel.joined && !channel.archived)
  );

  return {
    state,
    channels: computed(() => state.channels),
    joinedChannels: computed(() =>
      joined.value.filter(channel => channel.kind !== 'direct')
    ),
    directChannels: computed(() =>
      joined.value.filter(channel => channel.kind === 'direct')
    ),
    joinableChannels: computed(() =>
      state.channels.filter(
        channel => !channel.joined && channel.kind === 'public'
      )
    ),
    totalUnread: computed(() =>
      joined.value.reduce(
        (sum, channel) => sum + (channel.unread_count || 0),
        0
      )
    ),
    typingNames: channelId =>
      Object.values(state.typing[channelId] || {})
        .filter(entry => entry.until > Date.now())
        .map(entry => entry.name),
    findChannel,
    upsertChannel,
    reload: load,
    setActive: id => {
      state.activeChannelId = id;
      const channel = findChannel(id);
      if (channel) {
        channel.unread_count = 0;
        channel.mention_count = 0;
      }
    },
  };
}
